/**
 * useReservations Hook
 * Manages user's reservations with real-time updates
 */

import { useEffect, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { Reservation } from '@/types'
import { subscribeToUserReservations } from '@/services/reservation.service'

export function useReservations() {
  const { user } = useAuth()
  const [reservations, setReservations] = useState<Reservation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!user) {
      setReservations([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    const unsubscribe = subscribeToUserReservations(
      user.uid,
      reservations => {
        setReservations(reservations)
        setLoading(false)
      },
      error => {
        setError(error)
        setReservations([])
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [user])

  // Group reservations by wishlist
  const reservationsByWishlist = reservations.reduce(
    (acc, reservation) => {
      const wishlistId = reservation.wishlistId
      if (!acc[wishlistId]) {
        acc[wishlistId] = {
          wishlistTitle: reservation.wishlistTitle,
          items: [],
        }
      }
      acc[wishlistId].items.push(reservation)
      return acc
    },
    {} as Record<string, { wishlistTitle: string; items: Reservation[] }>
  )

  return {
    reservations,
    reservationsByWishlist,
    loading,
    error,
  }
}
